import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { PlusIcon, ArrowLeftIcon, ShareIcon } from '@heroicons/react/24/outline';
import Toast from '../components/Toast';
import { copyToClipboard } from '../utils/shareUtils';
import dayjs from 'dayjs';

// Add inline styles to prevent button focus highlight
const shareButtonStyles = `
  .share-button {
    background: none;
    border: none;
    padding: 0.5rem;
    color: rgb(156, 163, 175);
    cursor: pointer;
    border-radius: 0.5rem;
    transition: all 150ms ease;
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
  }
  .share-button:hover {
    color: rgb(220, 38, 38);
    background-color: rgb(254, 242, 242);
  }
  .share-button:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  .share-button:active {
    outline: none !important;
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('share-button-styles')) {
  const style = document.createElement('style');
  style.id = 'share-button-styles';
  style.textContent = shareButtonStyles;
  document.head.appendChild(style);
}

interface ShefuPuzzle {
  id: number;
  title: string;
  question_text: string | null;
  question_image_url: string | null;
  created_at: string;
  author: string;
}

export default function ShefuList() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [puzzles, setPuzzles] = useState<ShefuPuzzle[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    fetchPuzzles();
  }, [page]);

  const fetchPuzzles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/shefu?action=list&page=${page}&limit=20`);
      const data = await response.json();

      if (data.success) {
        setPuzzles(data.puzzles);
        setTotalPages(data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch shefu puzzles', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePuzzleClick = (id: number) => {
    navigate(`/academy/shefu/${id}`);
  };

  const handleCreateClick = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/academy/shefu/create' } } });
      return;
    }
    navigate('/academy/shefu/create');
  };

  const handleShare = async (e: React.MouseEvent, puzzleId: number) => {
    e.stopPropagation();
    e.preventDefault();

    // Blur the button to remove focus highlight on mobile
    const target = e.currentTarget as HTMLButtonElement;
    target.blur();

    const shareUrl = `${window.location.origin}/academy/shefu/${puzzleId}`;

    const success = await copyToClipboard(shareUrl);

    if (success) {
      setToast({
        message: '链接已复制！粘贴分享给朋友查看吧',
        type: 'success',
      });
    } else {
      setToast({
        message: '复制失败，请重试',
        type: 'error',
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <button
        onClick={() => navigate('/academy')}
        className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        <span>返回书院</span>
      </button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">每日射覆</h1>
        <button
          onClick={handleCreateClick}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>添加题目</span>
        </button>
      </div>

      <div className="text-sm text-gray-600 mb-6">
        <p>射覆是古代占卜的一种形式，题目是热心同修轮流出题。在这里，你可以查看和解答射覆题目。</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-2 text-gray-500">加载中...</p>
        </div>
      ) : puzzles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">暂无射覆题目</p>
          <button
            onClick={handleCreateClick}
            className="mt-4 text-red-600 hover:text-red-700"
          >
            成为第一个发布题目的人
          </button>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {puzzles.map((puzzle) => (
              <div
                key={puzzle.id}
                onClick={() => handlePuzzleClick(puzzle.id)}
                className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow relative flex flex-col"
              >
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {puzzle.title}
                </h3>

                {puzzle.question_image_url && (
                  <img
                    src={puzzle.question_image_url}
                    alt={puzzle.title}
                    className="w-full h-48 object-cover rounded-lg mb-3"
                  />
                )}

                {puzzle.question_text && (
                  <p className="text-gray-700 text-sm line-clamp-3 mb-3">
                    {puzzle.question_text}
                  </p>
                )}

                <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                  <div>
                    <span className="block">作者：{puzzle.author}</span>
                    <span className="block">{dayjs(puzzle.created_at).format('YYYY-MM-DD HH:mm')}</span>
                  </div>
                  <button
                    onClick={(e) => handleShare(e, puzzle.id)}
                    className="share-button"
                    title="分享题目"
                  >
                    <ShareIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                上一页
              </button>
              <span className="text-gray-700">
                第 {page} / {totalPages} 页
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                下一页
              </button>
            </div>
          )}
        </>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={1500}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
